import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { AuditLogPanel } from '../components/AuditLogPanel';
import { ScenarioTargetsCard } from '../components/ScenarioTargetsCard';
import type { ScenarioResult, ScenarioSnapshot, SessionState, Variable } from '../types';

interface Props {
  onScenarioChange: () => void;
}

export function ScenarioBuilderPage({ onScenarioChange }: Props) {
  const [results, setResults] = useState<ScenarioResult[]>([]);
  const [drivers, setDrivers] = useState<Variable[]>([]);
  const [session, setSession] = useState<SessionState | null>(null);
  const [snapshots, setSnapshots] = useState<ScenarioSnapshot[]>([]);
  const [editId, setEditId] = useState('');
  const [editVal, setEditVal] = useState('');
  const [editNote, setEditNote] = useState('');
  const [snapName, setSnapName] = useState('');
  const [snapNote, setSnapNote] = useState('');
  const [intensity, setIntensity] = useState(0.5);

  const load = async () => {
    const [r, vars, s, snaps] = await Promise.all([
      api.allResults(),
      api.gdpDrivers(),
      api.session(),
      api.snapshots(),
    ]);
    setResults(r);
    setDrivers(vars.slice(0, 50));
    setSession(s);
    setSnapshots(snaps);
    setIntensity(s.custom_interaction_intensity);
  };

  useEffect(() => { load().catch(console.error); }, []);

  const applyOverride = async () => {
    if (!editId || editVal === '') return;
    await api.setOverride(editId, parseFloat(editVal), editNote || undefined);
    onScenarioChange();
    setEditNote('');
    await load();
  };

  const applyIntensity = async () => {
    await api.setInteractionIntensity(intensity);
    onScenarioChange();
    await load();
  };

  const reset = async () => {
    await api.resetCustom();
    onScenarioChange();
    await load();
  };

  const saveSnapshot = async () => {
    if (!snapName.trim()) return;
    await api.saveSnapshot(snapName.trim(), snapNote || undefined);
    setSnapName('');
    setSnapNote('');
    await load();
  };

  const loadSnapshot = async (id: string) => {
    await api.loadSnapshot(id);
    onScenarioChange();
    await load();
  };

  const deleteSnapshot = async (id: string) => {
    await api.deleteSnapshot(id);
    await load();
  };

  return (
    <div>
      <h1 className="page-title">Scenario Builder</h1>
      <p className="page-subtitle">
        Construct a Custom Case, save local snapshots, and review the analyst assumption log.
      </p>

      {session && (
        <div className="card" style={{ fontSize: '0.85rem' }}>
          <strong>Current session:</strong> {session.selected_scenario}
          {' · '}{Object.keys(session.custom_overrides).length} override(s)
          {' · '}Interaction intensity: {session.custom_interaction_intensity.toFixed(2)}
        </div>
      )}

      <ScenarioTargetsCard onChange={() => { onScenarioChange(); load(); }} />

      <div className="card">
        <h3>Compare Scenarios</h3>
        <table className="data-table">
          <thead>
            <tr><th>Scenario</th><th>Simulated GDP</th><th>Direct (pp)</th><th>Interactions (pp)</th><th>Gap to 6%</th></tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.scenario_id}>
                <td>{r.scenario_name}</td>
                <td>{r.simulated_gdp_growth.toFixed(3)}%</td>
                <td>{r.direct_contribution_pp.toFixed(3)}</td>
                <td>{r.interaction_effects_pp.toFixed(3)}</td>
                <td>{r.gap_to_target_pp.toFixed(3)} pp</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Custom Case Editor</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
          <select className="field" value={editId} onChange={(e) => setEditId(e.target.value)}>
            <option value="">Select GDP-linked variable…</option>
            {drivers.map((v) => (
              <option key={v.id} value={v.id}>{v.id} — {v.name.slice(0, 40)}</option>
            ))}
          </select>
          <input className="field" type="number" step="any" placeholder="Custom value" value={editVal} onChange={(e) => setEditVal(e.target.value)} />
          <input className="field" placeholder="Analyst note (optional)" value={editNote} onChange={(e) => setEditNote(e.target.value)} />
          <button className="btn" type="button" onClick={applyOverride}>Apply</button>
          <button className="btn secondary" type="button" onClick={reset}>Reset Custom Case</button>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <label style={{ fontSize: '0.85rem' }}>Interaction intensity:</label>
          <input className="field" type="number" min={0} max={1} step={0.25} value={intensity} onChange={(e) => setIntensity(parseFloat(e.target.value))} style={{ width: 80 }} />
          <button className="btn secondary" type="button" onClick={applyIntensity}>Apply intensity</button>
        </div>
      </div>

      <div className="card">
        <h3>Saved Scenario Snapshots</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <input className="field" placeholder="Snapshot name" value={snapName} onChange={(e) => setSnapName(e.target.value)} />
          <input className="field" placeholder="Note (optional)" value={snapNote} onChange={(e) => setSnapNote(e.target.value)} />
          <button className="btn" type="button" onClick={saveSnapshot}>Save current session</button>
        </div>
        {snapshots.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>No snapshots saved yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Scenario</th><th>Overrides</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
              {snapshots.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.selected_scenario}</td>
                  <td>{Object.keys(s.custom_overrides).length}</td>
                  <td>{s.created_at?.slice(0, 19)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn secondary" type="button" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', marginRight: '0.25rem' }} onClick={() => loadSnapshot(s.id)}>Load</button>
                    <button className="btn secondary" type="button" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => deleteSnapshot(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AuditLogPanel />
    </div>
  );
}
